import { useEffect, useState } from "react";
import { apiGet } from "../shared/api.js";
import { copy } from "../shared/copy.js";
import { Button } from "./Button.js";
import { track } from "../shared/analytics.js";
import styles from "./ShareSheet.module.css";

interface Props {
  passportId: string;
  refCode: string;
  childName: string;
}

type TemplateKey = "a" | "b" | "c";

interface StatsResp {
  shared: number;
  booked: number;
}

// Absolute referral URL — the share-sheet target has to be openable
// from another device, so we build against window.location.origin
// (works for prod, localhost dev, and TG webview).
function referralUrl(refCode: string): string {
  return `${window.location.origin}/r/${encodeURIComponent(refCode)}`;
}

export function ShareSheet(props: Props) {
  const [pick, setPick] = useState<TemplateKey>("a");
  const [text, setText] = useState("");
  const [stats, setStats] = useState<StatsResp | null>(null);
  const [copied, setCopied] = useState(false);

  // Refresh the "переслали N · забронировали M" counter every time
  // the sheet mounts. Cheap query, worth the freshness.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await apiGet<StatsResp>(
          `/api/passport/${encodeURIComponent(props.passportId)}/referral-stats`,
        );
        if (!cancelled) setStats(r);
      } catch {
        // stats are decoration; failure is silent
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.passportId]);

  const templates: Record<TemplateKey, string> = {
    a: copy.s7.templateA(props.childName),
    b: copy.s7.templateB(props.childName),
    c: copy.s7.templateC(),
  };

  function choose(next: TemplateKey) {
    setPick(next);
    setText(templates[next]);
  }

  // Lazy init: fill the textarea with template A on first mount if the
  // user hasn't started editing yet.
  useEffect(() => {
    if (text === "") setText(templates.a);
    track({
      name: "share_opened",
      passportId: props.passportId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const url = referralUrl(props.refCode);
  const shareText = `${text}\n\n${url}`;

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      track({
        name: "share_copied",
        passportId: props.passportId,
        props: { template: pick },
      });
    } catch {
      // Older browsers — do nothing, the button still highlights
    }
  }

  function onShare() {
    track({
      name: "share_clicked",
      passportId: props.passportId,
      props: { template: pick, native: !!navigator.share },
    });
    if (navigator.share) {
      void navigator.share({ url, text });
    } else {
      void onCopy();
    }
  }

  return (
    <div className={styles.wrap}>
      <p className={styles.title}>{copy.s7.title}</p>
      <p className={styles.hint}>{copy.s7.hint}</p>

      <div className={styles.pickerRow}>
        {(["a", "b", "c"] as const).map((k) => (
          <button
            key={k}
            type="button"
            className={
              pick === k
                ? `${styles.pickBtn} ${styles.pickBtnActive}`
                : styles.pickBtn
            }
            onClick={() => choose(k)}
          >
            {copy.s7.picker[k]}
          </button>
        ))}
      </div>

      <textarea
        className={styles.textarea}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        maxLength={500}
      />

      <div className={styles.row}>
        <Button variant="secondary" onClick={onCopy}>
          {copied ? copy.s7.copied : copy.s7.copyLink}
        </Button>
        <Button block onClick={onShare}>
          {copy.s7.share}
        </Button>
      </div>

      {stats && (
        <p className={styles.stats}>
          {copy.s7.stats(stats.shared, stats.booked)}
        </p>
      )}
    </div>
  );
}
